import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatViews } from '../data/mockData';
import AdDisplay, { PrerollAd } from './AdDisplay';
import axios from 'axios';
import { ArrowLeft, Eye, Clock, ThumbsUp, ThumbsDown, Share2, Bookmark, Code, Copy, MessageSquare } from 'lucide-react';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const JoinButtons = () => {
  const [links, setLinks] = React.useState({});
  React.useEffect(() => {
    axios.get(BACKEND_URL + "/api/settings/public").then(r => setLinks(r.data || {})).catch(() => {});
  }, []);
  if (!links.social_whatsapp && !links.social_telegram) return null;
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
      {links.social_whatsapp && <a href={links.social_whatsapp} target="_blank" rel="noreferrer" style={{ padding: '6px 14px', borderRadius: 6, background: '#25D366', color: '#fff', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>Join WhatsApp</a>}
      {links.social_telegram && <a href={links.social_telegram} target="_blank" rel="noreferrer" style={{ padding: '6px 14px', borderRadius: 6, background: '#0088cc', color: '#fff', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>Join Telegram</a>}
    </div>
  );
};

const DownloadAd = () => {
  const [banner, setBanner] = React.useState(null);
  React.useEffect(() => {
    axios.get(BACKEND_URL + "/api/banners/active?position=download").then(r => {
      if (r.data && r.data.length > 0) setBanner(r.data[Math.floor(Math.random() * r.data.length)]);
    }).catch(() => {});
  }, []);
  if (!banner) return <p style={{color:'#555',fontSize:12}}>Loading ad...</p>;
  const handleClick = () => {
    axios.post(BACKEND_URL + "/api/banners/" + banner.id + "/click").catch(() => {});
    if (banner.target_url) window.open(banner.target_url, '_blank');
  };
  return (
    <div onClick={handleClick} style={{cursor:'pointer',width:'100%'}}>
      {banner.image_url ? <img src={banner.image_url} alt={banner.alt_text||'Ad'} style={{width:'100%',borderRadius:8,maxHeight:200,objectFit:'cover'}} /> : <p style={{color:'#888'}}>Advertisement</p>}
    </div>
  );
};

const VideoPage = ({ video, onBack }) => {
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [likeCount, setLikeCount] = useState(video.likes || 0);
  const [dislikeCount, setDislikeCount] = useState(video.dislikes || 0);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [replies, setReplies] = useState({});
  const [viewCount, setViewCount] = useState(video.views || 0);
  const [shared, setShared] = useState(false);
  const [showPreroll, setShowPreroll] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showDownloadAd, setShowDownloadAd] = useState(false);
  const [dlCountdown, setDlCountdown] = useState(10);
  const videoRef = React.useRef(null);
  const [allowComments, setAllowComments] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    // Check system settings
    axios.get(`${BACKEND_URL}/api/system/public`).then(r => {
      if (r.data && r.data.allow_comments === false) setAllowComments(false);
    }).catch(() => {});

    // Increment view
    axios.get(`${BACKEND_URL}/api/videos/${video.id}`).then(r => {
      setViewCount(r.data.views || 0);
      setLikeCount(r.data.likes || 0);
      setDislikeCount(r.data.dislikes || 0);
    }).catch(() => {});

    // Load comments + replies
    axios.get(`${BACKEND_URL}/api/comments/${video.id}`).then(async r => {
      const cmts = r.data.map(c => ({
        id: c.id, user: c.user || "Anonymous", text: c.text,
        time: timeAgo(c.created_at), likes: c.likes || 0
      }));
      setComments(cmts);
      const reps = {};
      for (const c of r.data) {
        try {
          const rr = await axios.get(`${BACKEND_URL}/api/comments/${c.id}/replies`);
          if (rr.data.length > 0) reps[c.id] = rr.data;
        } catch(e) {}
      }
      setReplies(reps);
    }).catch(() => {});

    // Related videos
    axios.get(`${BACKEND_URL}/api/videos/${video.id}/related`).then(r => {
      setRelated(r.data || []);
    }).catch(() => {});

    // Dynamic SEO
    document.title = `${video.title} - Raja Bokep`;
    let canon = document.querySelector("link[rel=canonical]");
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = `https://raja-bokep.com/video/${video.slug || video.id}`;
    // Track visit
    axios.post(`${BACKEND_URL}/api/track/visit`).catch(() => {});
  }, [video.id]);
  // Keyboard shortcuts for video
  useEffect(() => {
    const handleKey = (e) => {
      if (!videoRef.current || e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const v = videoRef.current;
      switch(e.key) {
        case ' ': case 'k': e.preventDefault(); v.paused ? v.play() : v.pause(); break;
        case 'ArrowLeft': case 'j': e.preventDefault(); v.currentTime -= 10; break;
        case 'ArrowRight': case 'l': e.preventDefault(); v.currentTime += 10; break;
        case 'ArrowUp': e.preventDefault(); v.volume = Math.min(1, v.volume + 0.1); break;
        case 'ArrowDown': e.preventDefault(); v.volume = Math.max(0, v.volume - 0.1); break;
        case 'f': e.preventDefault(); document.fullscreenElement ? document.exitFullscreen() : v.requestFullscreen(); break;
        case 'm': e.preventDefault(); v.muted = !v.muted; break;
        default: break;
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);


  const timeAgo = (dateStr) => {
    if (!dateStr) return 'Just now';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const handleLike = async () => {
    if (liked) return;
    try {
      const r = await axios.post(`${BACKEND_URL}/api/videos/${video.id}/like`);
      setLikeCount(r.data.likes); setLiked(true); setDisliked(false);
    } catch (err) {}
  };

  const handleDislike = async () => {
    if (disliked) return;
    try {
      const r = await axios.post(`${BACKEND_URL}/api/videos/${video.id}/dislike`);
      setDislikeCount(r.data.dislikes); setDisliked(true); setLiked(false);
    } catch (err) {}
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/comments/${video.id}`, { user: 'Visitor', text: comment.trim() });
      setComments([{ id: Date.now(), user: 'Visitor', text: comment.trim(), time: 'Just now', likes: 0 }, ...comments]);
      setComment('');
    } catch (err) {}
  };

  const handleShare = (platform) => {
    const url = `https://raja-bokep.com/?video=${video.id}`;
    const text = video.title;
    if (platform === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
    else if (platform === 'telegram') window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
    else if (platform === 'copy') {
      navigator.clipboard?.writeText(url);
      setShared(true); setTimeout(() => setShared(false), 2000);
    } else if (navigator.share) {
      navigator.share({ title: text, url });
    }
  };


  const handleDownload = () => {
    setShowDownloadAd(true);
    setDlCountdown(10);
  };

  useEffect(() => {
    if (showDownloadAd && dlCountdown > 0) {
      const t = setTimeout(() => setDlCountdown(dlCountdown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [showDownloadAd, dlCountdown]);

  const totalVotes = likeCount + dislikeCount;
  const rating = totalVotes > 0 ? Math.round((likeCount / totalVotes) * 100) : 0;

  return (
    <motion.div className="video-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35 }}>
      <motion.button className="vp-back" onClick={onBack} whileHover={{ x: -3 }} whileTap={{ scale: 0.97 }}>
        <ArrowLeft size={15} /> Back to videos
      </motion.button>

      <div className="vp-layout">
        <div className="vp-main">
          <motion.div className="vp-player" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.1 }}>
            {showPreroll && <PrerollAd onSkip={() => setShowPreroll(false)} />}
            <video
              ref={videoRef}
              src={video.video_url}
              controls autoPlay controlsList="nodownload" playsInline
              className="vp-iframe"
              style={{ background: '#000' }}

            />

          </motion.div>

          <AdDisplay position="below-player" />

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="vp-detail">
              <h1 className="vp-title">{video.title}</h1>
              <div className="vp-meta-row">
                <div className="vp-meta-left">
                  <span style={{display:'flex',alignItems:'center',gap:4}}><Eye size={14} /> {formatViews(viewCount)} Views</span>
                  <span style={{display:'flex',alignItems:'center',gap:4}}><Clock size={14} /> {video.duration}</span>
                  <span className="vp-tag">{video.category || 'Video'}</span>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span className="vp-rating-bar"><span className="vp-rating-fill" style={{width:`${rating}%`}} /></span>
                  <span className="vp-rating-text">{rating > 0 ? `${rating}%` : 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="vp-actions">
              <motion.button className={`vp-action-btn ${liked ? 'liked' : ''}`} onClick={handleLike} whileTap={{ scale: 0.97 }}>
                <ThumbsUp size={14} /> {likeCount > 0 ? likeCount : ''} Like
              </motion.button>
              <motion.button className={`vp-action-btn ${disliked ? 'disliked' : ''}`} onClick={handleDislike} whileTap={{ scale: 0.97 }}>
                <ThumbsDown size={14} /> {dislikeCount > 0 ? dislikeCount : ''} Dislike
              </motion.button>
              <motion.button className="vp-action-btn" onClick={() => setShowShareMenu(!showShareMenu)} whileTap={{ scale: 0.97 }}>
                <Share2 size={14} /> Share
              </motion.button>
              <motion.button className="vp-action-btn" onClick={handleDownload} whileTap={{ scale: 0.97 }}>
                <ArrowLeft size={14} style={{transform:'rotate(-90deg)'}} /> Download
              </motion.button>
            </div>

            {/* Share Menu Popup */}
            {showShareMenu && (
              <motion.div initial={{opacity:0,y:-5}} animate={{opacity:1,y:0}} style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:12,padding:12,background:'rgba(255,255,255,0.03)',borderRadius:10,border:'1px solid rgba(255,255,255,0.06)'}}>
                <button onClick={() => {handleShare('whatsapp');setShowShareMenu(false);}} style={{padding:'7px 14px',borderRadius:8,border:'none',background:'#25D366',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>WhatsApp</button>
                <button onClick={() => {handleShare('telegram');setShowShareMenu(false);}} style={{padding:'7px 14px',borderRadius:8,border:'none',background:'#0088cc',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>Telegram</button>
                <button onClick={() => {window.open('https://www.facebook.com/sharer/sharer.php?u='+encodeURIComponent('https://raja-bokep.com/?video='+video.id),'_blank');setShowShareMenu(false);}} style={{padding:'7px 14px',borderRadius:8,border:'none',background:'#1877F2',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>Facebook</button>
                <button onClick={() => {window.open('https://twitter.com/intent/tweet?url='+encodeURIComponent('https://raja-bokep.com/?video='+video.id)+'&text='+encodeURIComponent(video.title),'_blank');setShowShareMenu(false);}} style={{padding:'7px 14px',borderRadius:8,border:'none',background:'#1DA1F2',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>Twitter</button>
                <button onClick={() => {handleShare('copy');setShowShareMenu(false);}} style={{padding:'7px 14px',borderRadius:8,border:'none',background:'rgba(255,255,255,0.1)',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>{shared ? '✅ Copied!' : '📋 Salin Link'}</button>
              </motion.div>
            )}

            {/* Download Ad Popup */}
            {showDownloadAd && (
              <div style={{position:'fixed',inset:0,zIndex:99999,background:'rgba(0,0,0,0.85)',backdropFilter:'blur(8px)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                <div style={{background:'linear-gradient(135deg,#12121c,#1a1a28)',border:'1px solid rgba(255,60,60,0.15)',borderRadius:20,padding:'30px',textAlign:'center',maxWidth:420,width:'90%',boxShadow:'0 20px 60px rgba(0,0,0,0.5)'}}>
                  <p style={{fontSize:13,color:'#888',marginBottom:12}}>Tonton iklan untuk download video</p>
                  <div style={{background:'rgba(255,255,255,0.03)',borderRadius:12,padding:16,marginBottom:16,minHeight:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <DownloadAd />
                  </div>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginBottom:16}}>
                    <div style={{width:24,height:24,borderRadius:'50%',border:'2px solid rgba(255,60,60,0.3)',borderTopColor:'#ff3c3c',animation:'spin 1s linear infinite',display:dlCountdown>0?'block':'none'}} />
                    <span style={{fontSize:14,fontWeight:700,color:dlCountdown>0?'#ff3c3c':'#4caf50'}}>{dlCountdown > 0 ? 'Tunggu '+dlCountdown+' detik...' : '✅ Download siap!'}</span>
                  </div>
                  <div style={{display:'flex',gap:10,justifyContent:'center'}}>
                    <button onClick={()=>setShowDownloadAd(false)} style={{padding:'10px 24px',borderRadius:10,fontSize:13,fontWeight:700,background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.08)',color:'#aaa',cursor:'pointer',fontFamily:'Inter,sans-serif'}}>Batal</button>
                    {dlCountdown <= 0 && <a href={video.video_url} download style={{padding:'10px 24px',borderRadius:10,fontSize:13,fontWeight:700,background:'linear-gradient(135deg,#ff3c3c,#ff8a00)',border:'none',color:'#fff',cursor:'pointer',fontFamily:'Inter,sans-serif',textDecoration:'none',boxShadow:'0 4px 15px rgba(255,60,60,0.3)'}}>Download Video</a>}
                  </div>
                </div>
              </div>
            )}

            {/* Join Group buttons */}
            <JoinButtons />



            {/* Uploader */}
            <div className="vp-uploader">
              <div className="vp-avatar">{(video.uploader || 'U')[0]}</div>
              <div className="vp-uploader-info">
                <div className="vp-uploader-name">{video.uploader || 'Unknown'}</div>
                <div className="vp-uploader-desc">Content Creator</div>
              </div>
            </div>

            <p className="vp-desc">{video.description || `Watch this amazing ${(video.category || 'video').toLowerCase()} content.`}</p>

            {/* Tags */}
            {video.tags && video.tags.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {video.tags.map((tag, i) => (
                  <span key={i} style={{ padding: '4px 10px', background: 'rgba(245,158,11,0.08)', color: '#f59e0b', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>#{tag}</span>
                ))}
              </div>
            )}

            {/* Comments */}
            {allowComments && <div className="comments-box">
              <h3 className="comments-title"><MessageSquare size={15} /> Comments ({comments.length})</h3>
              <form onSubmit={handleComment} className="comment-form-row">
                <input type="text" placeholder="Add a comment..." value={comment} onChange={(e) => setComment(e.target.value)} className="comment-input" />
                <motion.button type="submit" className="comment-post-btn" whileTap={{ scale: 0.97 }}>Post</motion.button>
              </form>
              <AnimatePresence>
                {comments.length === 0 ? (
                  <p style={{ color: '#555', fontSize: 13, padding: '12px 0' }}>No comments yet. Be the first!</p>
                ) : comments.map(c => (
                  <motion.div key={c.id} className="comment-item" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
                    <div className="comment-avatar">{c.user[0]}</div>
                    <div className="comment-body">
                      <div className="comment-header">
                        <span className="comment-name">{c.user}</span>
                        <span className="comment-time">{c.time}</span>
                      </div>
                      <p className="comment-text">{c.text}</p>
                      <button className="comment-like"><ThumbsUp size={11} /> {c.likes}</button>
                      {replies[c.id] && replies[c.id].map(r => (
                        <div key={r.id} style={{marginTop:6,padding:"6px 10px",background:"rgba(245,158,11,0.06)",borderRadius:6,borderLeft:"2px solid #f59e0b"}}>
                          <span style={{fontSize:11,fontWeight:800,color:"#f59e0b"}}>Admin</span>
                          <span style={{fontSize:10,color:"#555",marginLeft:6}}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
                          <p style={{fontSize:12,color:"#bbb",marginTop:2}}>{r.text}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>}

            {/* Related Videos */}
            {related.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 800, color: '#fff', marginBottom: 12 }}>Related Videos</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                  {related.map(rv => (
                    <div key={rv.id} onClick={() => { window.scrollTo(0,0); window.location.href = `/?video=${rv.id}`; }}
                      style={{ cursor: 'pointer', borderRadius: 10, overflow: 'hidden', background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.04)', transition: 'all 0.2s' }}>
                      <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                        <img src={rv.thumbnail || '/logo.png'} alt={rv.title} loading="lazy"
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ padding: 8 }}>
                        <p style={{ fontSize: 11, fontWeight: 600, color: '#bbb', lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{rv.title}</p>
                        <p style={{ fontSize: 10, color: '#555', marginTop: 3 }}>{formatViews(rv.views || 0)} views</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default VideoPage;
